import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api, formatError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { DollarSign, Clock, Trash2, Plus } from "lucide-react";
import VerificationCard from "../components/VerificationCard";

export default function TeacherDashboard() {
  const { user, loading } = useAuth();
  const [profile, setProfile] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [services, setServices] = useState([]);
  const [form, setForm] = useState({ title: "", category: "astrology", description: "", duration_min: 30, price_usd: 50 });
  const [saving, setSaving] = useState(false);

  const refresh = () => {
    api.get("/gurus/me").then((r) => setProfile(r.data)).catch(() => {});
    api.get("/gurus/me/earnings").then((r) => setEarnings(r.data)).catch(() => {});
    api.get("/gurus/me/services").then((r) => setServices(r.data.items)).catch(() => {});
  };
  useEffect(() => { if (user?.role === "guru") refresh(); /* eslint-disable-next-line */ }, [user]);

  if (loading) return <div className="p-10">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "guru") return <Navigate to="/dashboard" replace />;

  const addService = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/gurus/services", { ...form, duration_min: parseInt(form.duration_min), price_usd: parseFloat(form.price_usd) });
      toast.success("Service added");
      setForm({ title: "", category: "astrology", description: "", duration_min: 30, price_usd: 50 });
      refresh();
    } catch (e) { toast.error(formatError(e)); }
    finally { setSaving(false); }
  };

  const deleteService = async (id) => {
    if (!window.confirm("Remove this service?")) return;
    await api.delete(`/gurus/services/${id}`);
    toast.success("Removed");
    refresh();
  };

  const statusColor = profile?.status === "approved" ? "bg-green-100 text-green-800"
    : profile?.status === "pending" ? "bg-amber-100 text-amber-800"
    : "bg-red-100 text-red-800";

  return (
    <div data-testid="teacher-dashboard" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <div className="overline">Namaste, {user.name}</div>
      <div className="flex items-center gap-3 mt-2 flex-wrap">
        <h1 className="font-serif text-5xl text-[#4A0E17]">Teacher Dashboard</h1>
        {profile && <span data-testid="profile-status" className={`px-3 py-1 rounded-full text-xs font-semibold capitalize ${statusColor}`}>{profile.status}</span>}
      </div>

      {earnings && (
        <div className="grid md:grid-cols-4 gap-5 mt-10">
          <div data-testid="earnings-total" className="bg-gradient-to-br from-[#4A0E17] to-[#36080F] text-[#FAF6F0] rounded-2xl p-6 relative overflow-hidden">
            <div className="absolute -right-5 -top-5 font-devanagari text-[120px] leading-none text-[#D4AF37] opacity-10">ॐ</div>
            <DollarSign className="w-6 h-6 text-[#D4AF37]" />
            <div className="overline text-[#D4AF37] mt-3">Total earned</div>
            <div className="font-mono text-3xl mt-1 font-semibold">${earnings.earnings.total.toFixed(2)}</div>
          </div>
          <div className="bg-[#FFFDF9] sacred-border rounded-2xl p-6">
            <div className="overline">Pending payout</div>
            <div className="font-mono text-3xl text-[#E65100] mt-1 font-semibold">${earnings.earnings.pending.toFixed(2)}</div>
          </div>
          <div className="bg-[#FFFDF9] sacred-border rounded-2xl p-6">
            <div className="overline">Paid out</div>
            <div className="font-mono text-3xl text-[#4A0E17] mt-1 font-semibold">${earnings.earnings.paid_out.toFixed(2)}</div>
          </div>
          <div className="bg-[#FFFDF9] sacred-border rounded-2xl p-6">
            <div className="overline">Platform fee</div>
            <div className="font-mono text-3xl text-[#63534E] mt-1 font-semibold">{earnings.earnings.platform_fee_percent}%</div>
            <div className="text-xs text-[#63534E] mt-2">You earn {100 - earnings.earnings.platform_fee_percent}% of each booking</div>
          </div>
        </div>
      )}

      <VerificationCard profile={profile} onDone={refresh} />
      <section className="mt-14 grid lg:grid-cols-2 gap-8">
        <div>
          <h2 className="font-serif text-2xl text-[#4A0E17]">Your services</h2>
          {services.length === 0 ? (
            <div className="mt-5 bg-[#FFFDF9] sacred-border rounded-2xl p-8 text-center text-[#63534E]">No services yet. Add your first on the right.</div>
          ) : (
            <div className="mt-5 space-y-3">
              {services.filter((s) => s.active).map((s) => (
                <div key={s.id} data-testid={`service-row-${s.id}`} className="bg-[#FFFDF9] sacred-border rounded-xl p-4 flex items-start gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 text-xs text-[#63534E]">
                      <span className="capitalize bg-[#F3E5AB]/50 px-2 rounded-full">{s.category}</span>
                      <Clock className="w-3.5 h-3.5" /> {s.duration_min} min
                      <span className="font-mono text-[#E65100] font-semibold">${s.price_usd.toFixed(0)}</span>
                    </div>
                    <div className="font-serif text-lg text-[#4A0E17] mt-1">{s.title}</div>
                    <p className="text-sm text-[#63534E] mt-1 line-clamp-2">{s.description}</p>
                  </div>
                  <button data-testid={`delete-service-${s.id}`} onClick={() => deleteService(s.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <form onSubmit={addService} className="bg-[#FFFDF9] sacred-border rounded-2xl p-6 h-fit space-y-4">
          <h3 className="font-serif text-xl text-[#4A0E17]">Add a service</h3>
          {profile?.status !== "approved" && (
            <div className="bg-amber-50 border border-amber-300 rounded-xl p-3 text-sm text-amber-800">
              Services become publicly bookable once your profile is approved by admin.
            </div>
          )}
          <Input data-testid="svc-title" required placeholder="e.g. Birth-chart reading (60 min)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-white border-[#D4AF37]/40 h-11" />
          <select data-testid="svc-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full h-11 rounded-md border border-[#D4AF37]/40 px-3 bg-white text-[#1F1412]">
            {["astrology","aarti","puja","lecture","bhajan","music","wedding","household","event","vastu","tarot","other"].map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <Textarea data-testid="svc-description" required rows={3} placeholder="What devotees can expect…" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-white border-[#D4AF37]/40" />
          <div className="grid grid-cols-2 gap-3">
            <Input data-testid="svc-duration" type="number" min="5" placeholder="Duration (min)" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: e.target.value })} className="bg-white border-[#D4AF37]/40 h-11" />
            <Input data-testid="svc-price" type="number" min="1" step="1" placeholder="Price (USD)" value={form.price_usd} onChange={(e) => setForm({ ...form, price_usd: e.target.value })} className="bg-white border-[#D4AF37]/40 h-11" />
          </div>
          <Button data-testid="svc-add" type="submit" disabled={saving || profile?.status !== "approved"} className="w-full bg-[#E65100] hover:bg-[#BF360C] rounded-full h-11">
            {saving ? "Adding…" : <><Plus className="w-4 h-4 mr-1" /> Add service</>}
          </Button>
        </form>
      </section>

      {earnings?.bookings?.length > 0 && (
        <section className="mt-14">
          <h2 className="font-serif text-2xl text-[#4A0E17]">Recent bookings</h2>
          <div className="mt-5 bg-[#FFFDF9] sacred-border rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#4A0E17] text-[#F3E5AB]"><tr>
                <th className="text-left px-4 py-3">Service</th>
                <th className="text-left px-4 py-3">Booker</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">You earn</th>
                <th className="text-left px-4 py-3">Date</th>
              </tr></thead>
              <tbody>
                {earnings.bookings.map((b) => (
                  <tr key={b.id} className="border-t border-[#D4AF37]/20">
                    <td className="px-4 py-3">{b.service_title}</td>
                    <td className="px-4 py-3">{b.booker_name}<br/><span className="text-xs text-[#63534E] font-mono">{b.booker_email}</span></td>
                    <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs ${b.payment_status === "paid" ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`}>{b.payment_status}</span></td>
                    <td className="px-4 py-3 text-right font-mono font-semibold text-[#E65100]">${b.guru_earning?.toFixed(2) || "—"}</td>
                    <td className="px-4 py-3 font-mono text-xs">{new Date(b.created_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
